import { useState } from 'react';
import useSWR from 'swr';
import { get } from '@/lib/api';
import { formatRelative } from '@/lib/time';
import { KpiTile } from '@/components/domain/KpiTile';
import { Skeleton } from '@/components/ui/skeleton';

// Read-only port of admin-dashboard's ops.html (parity roadmap workstream C):
// webhook KPIs, error events, reconciliation reports and the inventory
// forecast. The errorEvents "resolve" action deliberately stays in
// admin-dashboard — this page only displays resolved state.

type OpsError = {
  id: string;
  severity?: string;
  service?: string;
  action?: string;
  message?: string;
  timestamp?: string;
  resolved?: boolean;
};
type ErrorsPayload = { errors: OpsError[]; count: number };

type WebhookEvent = {
  service?: string;
  topic?: string;
  orderId?: string | number;
  orderNumber?: string | number;
  status?: string;
  duplicateCount?: number;
  receivedAt?: string;
};
type WebhooksPayload = {
  stats: { total: number; successful: number; failed: number; duplicates: number; processing: number };
  byService: Record<string, { total: number; success: number; error: number }>;
  recentEvents: WebhookEvent[];
};

type ReconIssueMissing = { orderName?: string; totalPrice?: string | number; customer?: string };
type ReconIssueFailed = { orderName?: string; totalPrice?: string | number; webhookError?: string };
type ReconReport = {
  id: string;
  runAt?: string;
  lookbackHours?: number;
  totalShopifyOrders?: number;
  healthy?: number;
  missingCount?: number;
  webhookFailedCount?: number;
  missing?: ReconIssueMissing[];
  webhookFailed?: ReconIssueFailed[];
};
type ReconPayload = { reports: ReconReport[] };

type OutlookSize = {
  size?: string;
  inventorySize?: string;
  isWomensSize?: boolean;
  onHand?: number;
  units?: number;
  expectedUnits?: number;
  buffer?: number;
  safetyStock?: number;
  totalNeeded?: number;
  buffered?: number;
  gap?: number;
  surplus?: number;
  fallbackToStandard?: boolean;
  spillOutToStandard?: number;
  spillInFromWomens?: number;
};
type OutlookProduct = {
  productName?: string;
  isUnisex?: boolean;
  totalOnHand?: number;
  totalNeeded?: number;
  totalBuffered?: number;
  totalSafetyStock?: number;
  totalGap?: number;
  totalSurplus?: number;
  totalCost?: number;
  sizes?: OutlookSize[];
};
type OutlookSubmission = {
  fundraiserName?: string;
  adminName?: string;
  sportType?: string;
  teamGender?: string;
  usedFallbackModel?: boolean;
  expectedUnits?: number;
  unitsUsed?: number;
  status?: string;
  tier1ProductName?: string;
  tier1StyleCode?: string;
  tier1Units?: number;
  tier2ProductName?: string;
  tier2StyleCode?: string;
  tier2Units?: number;
};
type OutlookPayload = {
  success?: boolean;
  fundraiserCount?: number;
  totalNeeded?: number;
  totalBuffered?: number;
  totalOnHand?: number;
  totalGap?: number;
  totalSurplus?: number;
  totalSafetyStock?: number;
  totalEstimatedCost?: number;
  currentSeasonalFactor?: number;
  seasonalOutlook?: { monthName: string; factor: number }[];
  bufferPct?: number;
  serviceLevel?: number;
  leadTimeDays?: number;
  byStyleCode?: Record<string, OutlookProduct>;
  submissions?: OutlookSubmission[];
  includedWithFallback?: number;
  excluded?: { missingForecastFields?: number; stale?: number; test?: number; alreadyOrdered?: number; maxAgeDays?: number };
  generatedAt?: string;
};

const LOOKBACKS = [
  { hours: 6, label: '6h' },
  { hours: 24, label: '24h' },
  { hours: 48, label: '48h' },
  { hours: 168, label: '7d' },
] as const;

function money(n?: number): string {
  if (typeof n !== 'number') return '—';
  return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function Chip({ tone, children }: { tone: 'success' | 'danger' | 'warning' | 'muted'; children: React.ReactNode }) {
  const cls = {
    success: 'bg-success/10 text-success',
    danger: 'bg-danger/10 text-danger',
    warning: 'bg-warning/10 text-warning',
    muted: 'bg-bg text-muted',
  }[tone];
  return <span className={`inline-flex items-center whitespace-nowrap rounded-sm px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}>{children}</span>;
}

function StatusDot({ tone }: { tone: 'success' | 'danger' | 'warning' }) {
  const cls = { success: 'bg-success', danger: 'bg-danger', warning: 'bg-warning' }[tone];
  return <span className={`inline-block h-2 w-2 rounded-full ${cls}`} />;
}

function SectionCard({ title, badge, children }: { title: string; badge?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <div className="mb-2 flex items-center gap-2">
        <h2 className="text-[13px] font-semibold tracking-tight">{title}</h2>
        {badge}
      </div>
      <div className="rounded-md bg-surface shadow-card">{children}</div>
    </div>
  );
}

function LoadingRows() {
  return (
    <div className="space-y-2 p-3">
      <Skeleton className="h-5 w-full" />
      <Skeleton className="h-5 w-5/6" />
      <Skeleton className="h-5 w-2/3" />
    </div>
  );
}

function EmptyRow({ children }: { children: React.ReactNode }) {
  return <div className="p-6 text-center text-[12px] text-muted">{children}</div>;
}

function severityTone(severity?: string): 'danger' | 'warning' | 'muted' {
  if (severity === 'critical' || severity === 'error') return 'danger';
  if (severity === 'warning') return 'warning';
  return 'muted';
}

function statusTone(status?: string): 'success' | 'danger' | 'warning' {
  if (status === 'error') return 'danger';
  if (status === 'processing') return 'warning';
  return 'success';
}

// Surplus below 30% of need shows amber — same threshold ops.html uses.
function healthTone(gap?: number, surplus?: number, needed?: number): 'success' | 'danger' | 'warning' {
  if ((gap || 0) > 0) return 'danger';
  if ((surplus || 0) < (needed || 1) * 0.3) return 'warning';
  return 'success';
}

export default function Ops() {
  const [hours, setHours] = useState<number>(24);

  const { data: errors, isLoading: errorsLoading, error: errorsError } = useSWR<ErrorsPayload>(
    `/api/ops/errors?hours=${hours}`, get, { refreshInterval: 60_000 });
  const { data: webhooks, isLoading: webhooksLoading, error: webhooksError } = useSWR<WebhooksPayload>(
    `/api/ops/webhooks?hours=${hours}`, get, { refreshInterval: 60_000 });
  const { data: recon, isLoading: reconLoading, error: reconError } = useSWR<ReconPayload>(
    '/api/ops/reconciliation', get, { refreshInterval: 60_000 });
  const { data: outlook, isLoading: outlookLoading, error: outlookError } = useSWR<OutlookPayload>(
    '/api/ops/inventory-outlook', get, { refreshInterval: 60_000 });

  const unresolved = (errors?.errors || []).filter((e) => !e.resolved).length;
  const stats = webhooks?.stats;
  const byService = Object.entries(webhooks?.byService || {});

  return (
    <div className="px-7 py-6">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-semibold tracking-tighter">Ops</h1>
          <p className="text-[12px] text-muted">Errors, webhooks, reconciliation and the inventory forecast · read-only · refreshes every minute</p>
        </div>
        <div className="flex gap-1.5">
          {LOOKBACKS.map((l) => (
            <button
              key={l.hours}
              onClick={() => setHours(l.hours)}
              className={`rounded-sm px-2.5 py-1 text-[11px] ${hours === l.hours ? 'bg-primary text-white' : 'bg-surface text-text/75 border border-border hover:bg-bg'}`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {/* Webhook KPI tiles */}
      <div className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <KpiTile label="Webhooks"   value={stats?.total}      loading={webhooksLoading} />
        <KpiTile label="Successful" value={stats?.successful} loading={webhooksLoading} />
        <KpiTile label="Failed"     value={stats?.failed}     loading={webhooksLoading} />
        <KpiTile label="Duplicates" value={stats?.duplicates} loading={webhooksLoading} />
      </div>
      {byService.length > 0 && (
        <div className="mb-5 flex flex-wrap gap-1.5">
          {byService.map(([svc, s]) => (
            <span key={svc} className={`rounded-sm border border-border bg-surface px-2 py-0.5 text-[10.5px] ${s.error > 0 ? 'text-danger' : 'text-muted'}`}>
              {svc.replace('webhook', '')} · {s.total}{s.error > 0 ? ` (${s.error} failed)` : ''}
            </span>
          ))}
        </div>
      )}

      {/* Errors */}
      <SectionCard
        title="Errors"
        badge={
          errorsLoading ? undefined : unresolved > 0
            ? <Chip tone="danger">{unresolved} unresolved</Chip>
            : <Chip tone="success">all clear</Chip>
        }
      >
        {errorsError ? (
          <EmptyRow>Couldn't load errors.</EmptyRow>
        ) : errorsLoading ? (
          <LoadingRows />
        ) : (errors?.errors || []).length === 0 ? (
          <EmptyRow>No errors in the last {hours}h.</EmptyRow>
        ) : (
          <div className="divide-y divide-divider">
            <div className="grid grid-cols-[70px_minmax(110px,0.7fr)_minmax(90px,0.6fr)_2fr_52px_64px] items-center gap-2 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.05em] text-subtle">
              <div>Severity</div><div>Service</div><div>Action</div><div>Message</div><div>When</div><div></div>
            </div>
            {(errors?.errors || []).map((e) => (
              <div key={e.id} className="grid grid-cols-[70px_minmax(110px,0.7fr)_minmax(90px,0.6fr)_2fr_52px_64px] items-center gap-2 px-3 py-1.5 text-[11.5px]">
                <div><Chip tone={severityTone(e.severity)}>{e.severity || '—'}</Chip></div>
                <div className="truncate font-medium">{e.service || '—'}</div>
                <div className="truncate text-muted">{e.action || '—'}</div>
                <div className="truncate text-muted" title={e.message || ''}>{e.message || '—'}</div>
                <div className="text-subtle">{e.timestamp ? formatRelative(e.timestamp) : '—'}</div>
                <div>{e.resolved ? <Chip tone="success">Resolved</Chip> : <Chip tone="danger">Open</Chip>}</div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Webhook events */}
      <SectionCard
        title="Webhook Events"
        badge={
          webhooksLoading || !stats ? undefined : stats.failed > 0
            ? <Chip tone="danger">{stats.failed} failed</Chip>
            : <Chip tone="success">{stats.total} processed</Chip>
        }
      >
        {webhooksError ? (
          <EmptyRow>Couldn't load webhook events.</EmptyRow>
        ) : webhooksLoading ? (
          <LoadingRows />
        ) : (webhooks?.recentEvents || []).length === 0 ? (
          <EmptyRow>No webhook events in the last {hours}h.</EmptyRow>
        ) : (
          <div className="divide-y divide-divider">
            <div className="grid grid-cols-[minmax(80px,0.7fr)_minmax(110px,1fr)_minmax(100px,1fr)_70px_82px_46px_52px] items-center gap-2 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.05em] text-subtle">
              <div>Service</div><div>Topic</div><div>Order ID</div><div>Order #</div><div>Status</div><div>Dupes</div><div>When</div>
            </div>
            {(webhooks?.recentEvents || []).map((e, i) => (
              <div key={`${e.orderId}-${e.topic}-${i}`} className="grid grid-cols-[minmax(80px,0.7fr)_minmax(110px,1fr)_minmax(100px,1fr)_70px_82px_46px_52px] items-center gap-2 px-3 py-1.5 text-[11.5px]">
                <div className="truncate font-medium">{(e.service || '—').replace('webhook', '')}</div>
                <div className="truncate text-muted">{e.topic || '—'}</div>
                <div className="truncate font-mono text-[10.5px] text-muted">{e.orderId ?? '—'}</div>
                <div className="text-muted">{e.orderNumber || '—'}</div>
                <div><Chip tone={statusTone(e.status)}>{e.status || '—'}</Chip></div>
                <div className={(e.duplicateCount || 0) > 0 ? 'font-semibold text-warning' : 'text-subtle'}>
                  {(e.duplicateCount || 0) > 0 ? `${e.duplicateCount}x` : '—'}
                </div>
                <div className="text-subtle">{e.receivedAt ? formatRelative(e.receivedAt) : '—'}</div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Reconciliation */}
      <div className="mb-5">
        <h2 className="mb-2 text-[13px] font-semibold tracking-tight">Order Reconciliation</h2>
        {reconError ? (
          <div className="rounded-md bg-surface p-6 text-center text-[12px] text-muted shadow-card">Couldn't load reports.</div>
        ) : reconLoading ? (
          <div className="rounded-md bg-surface shadow-card"><LoadingRows /></div>
        ) : (recon?.reports || []).length === 0 ? (
          <div className="rounded-md bg-surface p-6 text-center text-[12px] text-muted shadow-card">
            No reconciliation reports yet. The /reconcile job needs to run first.
          </div>
        ) : (
          <div className="space-y-2">
            {(recon?.reports || []).map((r) => {
              const issueCount = (r.missingCount || 0) + (r.webhookFailedCount || 0);
              return (
                <div key={r.id} className="rounded-md bg-surface p-3 shadow-card">
                  <div className="flex items-center gap-2">
                    <span className="text-[12.5px] font-semibold">{r.id}</span>
                    {issueCount > 0 ? <Chip tone="danger">{issueCount} issue{issueCount === 1 ? '' : 's'}</Chip> : <Chip tone="success">All clear</Chip>}
                  </div>
                  <div className="mt-1 text-[11px] text-muted">
                    {r.totalShopifyOrders ?? 0} Shopify orders checked · {r.healthy ?? 0} healthy · Lookback: {r.lookbackHours ?? '—'}h · {r.runAt ? formatRelative(r.runAt) : '—'}
                  </div>
                  {issueCount > 0 && (
                    <div className="mt-2 space-y-1 border-t border-divider pt-2 text-[11px]">
                      {(r.missing || []).map((m, i) => (
                        <div key={`m${i}`}>
                          <span className="font-medium">{m.orderName || '—'}</span> — ${m.totalPrice ?? '?'} — {m.customer || '—'} — <span className="font-semibold text-danger">Missed</span>
                        </div>
                      ))}
                      {(r.webhookFailed || []).map((f, i) => (
                        <div key={`f${i}`}>
                          <span className="font-medium">{f.orderName || '—'}</span> — ${f.totalPrice ?? '?'} — <span className="font-semibold text-warning">{f.webhookError || 'webhook failed'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Inventory outlook */}
      <div className="mb-5">
        <h2 className="mb-2 text-[13px] font-semibold tracking-tight">Inventory Forecast</h2>
        {outlookError ? (
          <div className="rounded-md bg-surface p-6 text-center text-[12px] text-muted shadow-card">
            Couldn't load the forecast. {(outlookError as Error)?.message || ''}
          </div>
        ) : outlookLoading ? (
          <div className="rounded-md bg-surface shadow-card"><LoadingRows /></div>
        ) : !outlook?.success || !outlook.fundraiserCount ? (
          <div className="rounded-md bg-surface p-6 text-center text-[12px] text-muted shadow-card">
            No active submissions with forecast data yet.
          </div>
        ) : (
          <OutlookView data={outlook} />
        )}
      </div>
    </div>
  );
}

function OutlookView({ data }: { data: OutlookPayload }) {
  const needed = data.totalNeeded || data.totalBuffered || 0;
  const tone = healthTone(data.totalGap, data.totalSurplus, needed);
  const healthLabel = tone === 'danger' ? 'Shortfall' : tone === 'warning' ? 'Low' : 'Healthy';
  const healthDetail = (data.totalGap || 0) > 0 ? `${data.totalGap} units short` : `${data.totalSurplus || 0} surplus`;

  const factor = data.currentSeasonalFactor || 1;
  const seasonLabel = factor >= 2 ? 'Peak season' : factor >= 0.8 ? 'Active' : factor >= 0.4 ? 'Moderate' : 'Slow season';
  const seasonTone: 'success' | 'danger' | 'warning' | 'muted' = factor >= 2 ? 'danger' : factor >= 0.8 ? 'success' : factor >= 0.4 ? 'warning' : 'muted';

  const ex = data.excluded;
  const exParts: string[] = [];
  if (ex?.test) exParts.push(`${ex.test} test submission${ex.test > 1 ? 's' : ''}`);
  if (ex?.alreadyOrdered) exParts.push(`${ex.alreadyOrdered} already ordered`);
  if (ex?.stale) exParts.push(`${ex.stale} older than ${ex.maxAgeDays || 180}d`);

  const products = Object.entries(data.byStyleCode || {});
  const maxFactor = Math.max(...(data.seasonalOutlook || []).map((s) => Number(s.factor) || 0), 1);

  return (
    <div>
      <div className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-5">
        <KpiTile label="Open Fundraisers" value={data.fundraiserCount} />
        <KpiTile label="Total Needed" value={needed} />
        <KpiTile label="On Hand" value={data.totalOnHand || 0} />
        <div className="rounded-md bg-surface p-3 shadow-card">
          <div className="text-[10px] font-medium uppercase tracking-[0.05em] text-muted">Health</div>
          <div className={`mt-1 text-[15px] font-semibold tracking-tight ${tone === 'danger' ? 'text-danger' : tone === 'warning' ? 'text-warning' : 'text-success'}`}>{healthLabel}</div>
          <div className="text-[10.5px] text-subtle">{healthDetail}</div>
        </div>
        <div className="rounded-md bg-surface p-3 shadow-card">
          <div className="text-[10px] font-medium uppercase tracking-[0.05em] text-muted">Season</div>
          <div className={`mt-1 text-[15px] font-semibold tracking-tight ${seasonTone === 'muted' ? 'text-subtle' : seasonTone === 'danger' ? 'text-danger' : seasonTone === 'warning' ? 'text-warning' : 'text-success'}`}>{seasonLabel}</div>
          <div className="text-[10.5px] text-subtle">{factor}x avg</div>
        </div>
      </div>

      {exParts.length > 0 && (
        <div className="mb-2 rounded-md bg-warning/10 px-3 py-2 text-[11px] text-text/80">
          Not counted in this forecast: {exParts.join(' · ')}
        </div>
      )}
      {(data.includedWithFallback || 0) > 0 && (
        <div className="mb-2 rounded-md bg-bg px-3 py-2 text-[11px] text-muted">
          <strong className="text-text">{data.includedWithFallback}</strong> included with fallback size mix (missing sport and/or gender)
        </div>
      )}

      {(data.seasonalOutlook || []).length > 0 && (
        <div className="mb-2 rounded-md bg-surface p-3 shadow-card">
          <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.05em] text-muted">Seasonal Outlook</div>
          <div className="flex items-end gap-2 overflow-x-auto">
            {(data.seasonalOutlook || []).map((s) => {
              const f = Number(s.factor) || 0;
              const h = Math.max(8, (f / maxFactor) * 56);
              const barCls = f >= 2 ? 'bg-danger' : f >= 0.8 ? 'bg-success' : f >= 0.4 ? 'bg-warning' : 'bg-border';
              return (
                <div key={s.monthName} className="flex min-w-[34px] flex-col items-center gap-1">
                  <div className={`w-5 rounded-sm ${barCls}`} style={{ height: `${h}px` }} />
                  <div className="text-[9.5px] text-subtle">{s.monthName}</div>
                  <div className="text-[9.5px] font-medium text-muted">{s.factor}x</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 rounded-md bg-surface px-3 py-2 text-[11px] text-muted shadow-card">
        <span>Buffer: <strong className="text-text">{data.bufferPct ?? 10}%</strong></span>
        <span>Service Level: <strong className="text-text">{((data.serviceLevel ?? 0.95) * 100).toFixed(0)}%</strong></span>
        <span>Lead Time: <strong className="text-text">{data.leadTimeDays ?? 14} days</strong></span>
        <span>Safety Stock: <strong className="text-text">{data.totalSafetyStock ?? 0} units</strong></span>
        {typeof data.totalEstimatedCost === 'number' && <span>Est. Cost: <strong className="text-text">{money(data.totalEstimatedCost)}</strong></span>}
      </div>

      {products.map(([code, p]) => {
        const pNeeded = p.totalNeeded || p.totalBuffered || 0;
        const pTone = healthTone(p.totalGap, p.totalSurplus, pNeeded);
        return (
          <div key={code} className="mb-2 rounded-md bg-surface shadow-card">
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
              <div className="flex items-center gap-2 text-[12.5px] font-semibold">
                <StatusDot tone={pTone} />
                <span>{code} — {p.productName || 'Unknown product'}</span>
                {p.isUnisex && <Chip tone="muted">Unisex</Chip>}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted">
                <span>On Hand: <strong className="text-text">{p.totalOnHand ?? 0}</strong></span>
                <span>Needed: <strong className="text-text">{pNeeded}</strong></span>
                {!!p.totalSafetyStock && <span>Safety: <strong className="text-text">{p.totalSafetyStock}</strong></span>}
                {(p.totalGap || 0) > 0
                  ? <span className="font-semibold text-danger">Gap: {p.totalGap}</span>
                  : <span className="font-semibold text-success">Surplus: {p.totalSurplus ?? 0}</span>}
                {!!p.totalCost && <span>Cost: <strong className="text-text">{money(p.totalCost)}</strong></span>}
              </div>
            </div>
            <div className="divide-y divide-divider border-t border-divider">
              <div className="grid grid-cols-[minmax(76px,1fr)_repeat(5,minmax(58px,0.7fr))_minmax(86px,0.8fr)_36px] items-center gap-2 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.05em] text-subtle">
                <div>Size</div><div>On Hand</div><div>Expected</div><div>Buffer</div><div>Safety</div><div>Needed</div><div>Gap / Surplus</div><div></div>
              </div>
              {(p.sizes || []).map((s, i) => {
                const gap = s.gap || 0;
                const surplus = s.surplus || 0;
                const sNeeded = s.totalNeeded || s.buffered || 0;
                const sTone = healthTone(gap, surplus, sNeeded);
                return (
                  <div key={`${s.size}-${i}`} className="grid grid-cols-[minmax(76px,1fr)_repeat(5,minmax(58px,0.7fr))_minmax(86px,0.8fr)_36px] items-center gap-2 px-3 py-1.5 text-[11.5px]">
                    <div className="font-medium">
                      {s.size}{s.isWomensSize ? ' ♀' : ''}
                      {s.fallbackToStandard && (
                        <span className="ml-1 text-[10px] text-subtle" title="Women's stock can't cover this — unmet units added to the standard size">
                          ↪ {s.spillOutToStandard || ''} → {s.inventorySize}
                        </span>
                      )}
                    </div>
                    <div>{s.onHand ?? 0}</div>
                    <div>{s.units ?? s.expectedUnits ?? 0}</div>
                    <div>{s.buffer ?? 0}</div>
                    <div>{s.safetyStock ?? 0}</div>
                    <div className="font-semibold">
                      {sNeeded}
                      {!!s.spillInFromWomens && (
                        <span className="ml-1 font-normal text-subtle" title="Includes unmet women's-size demand that buyers will fill from this size">(+{s.spillInFromWomens} ♀)</span>
                      )}
                    </div>
                    <div className={`font-semibold ${gap > 0 ? 'text-danger' : 'text-success'}`}>{gap > 0 ? `-${gap}` : `+${surplus}`}</div>
                    <div><StatusDot tone={sTone} /></div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {(data.submissions || []).length > 0 && (
        <div className="mb-2 rounded-md bg-surface shadow-card">
          <div className="px-3 py-2 text-[12.5px] font-semibold">
            Contributing Fundraisers <span className="font-normal text-subtle">({data.submissions!.length})</span>
          </div>
          <div className="divide-y divide-divider border-t border-divider">
            <div className="grid grid-cols-[minmax(120px,1.4fr)_minmax(90px,1fr)_minmax(80px,0.8fr)_64px_60px_minmax(90px,0.9fr)_minmax(110px,1.1fr)_minmax(110px,1.1fr)] items-center gap-2 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.05em] text-subtle">
              <div>Fundraiser</div><div>Admin</div><div>Sport</div><div>Gender</div><div>Units</div><div>Status</div><div>Tier 1</div><div>Tier 2</div>
            </div>
            {(data.submissions || []).map((sub, i) => {
              const tierCell = (name?: string, codeStr?: string, units?: number) => {
                if (!name && !codeStr) return <span className="text-subtle">—</span>;
                return (
                  <span title={codeStr || ''} className="truncate">
                    {name || codeStr}
                    {typeof units === 'number' && <span className="text-subtle"> · {units}u</span>}
                  </span>
                );
              };
              return (
                <div key={i} className="grid grid-cols-[minmax(120px,1.4fr)_minmax(90px,1fr)_minmax(80px,0.8fr)_64px_60px_minmax(90px,0.9fr)_minmax(110px,1.1fr)_minmax(110px,1.1fr)] items-center gap-2 px-3 py-1.5 text-[11.5px]">
                  <div className="truncate font-medium">{sub.fundraiserName || '—'}</div>
                  <div className="truncate text-muted">{sub.adminName || '—'}</div>
                  <div className="capitalize text-muted">
                    {(sub.sportType || '—').replace(/_/g, ' ')}
                    {sub.usedFallbackModel && <span className="ml-1 text-[10px] text-subtle">fallback</span>}
                  </div>
                  <div className="capitalize text-muted">{sub.teamGender || '—'}</div>
                  <div className="text-muted">{sub.expectedUnits ?? (sub.unitsUsed ? `${sub.unitsUsed} (est)` : '—')}</div>
                  <div><Chip tone="muted">{(sub.status || '—').replace(/_/g, ' ')}</Chip></div>
                  <div className="flex min-w-0">{tierCell(sub.tier1ProductName, sub.tier1StyleCode, sub.tier1Units)}</div>
                  <div className="flex min-w-0">{tierCell(sub.tier2ProductName, sub.tier2StyleCode, sub.tier2Units)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.generatedAt && (
        <div className="text-[10.5px] text-subtle">
          Generated {new Date(data.generatedAt).toLocaleString()} · Buffer {data.bufferPct ?? 10}% · Service Level {((data.serviceLevel ?? 0.95) * 100).toFixed(0)}% · Lead Time {data.leadTimeDays ?? 14}d
        </div>
      )}
    </div>
  );
}
